import { Controller } from "stimulus"

// Toggles a boolean flag on a user straight from an admin table row. The flag
// name comes from the checkbox's data-flag attribute and drives both the
// endpoint and the request body, so one controller serves any user flag.
//
// test_user is confirmed via #testUserToggleModal in both directions, since
// either one immediately changes what counts in reports. Other flags this
// controller ends up driving apply right away.
const TEST_USER_COPY = {
  enable: {
    title: "Mark as Test User",
    message: (name) => `Mark ${name} as a Test User?`,
    consequences: [
      "The user stays active and usable",
      "Excluded from reports, usage metrics, and organization cost calculations",
      "Reversible at any time by turning this off"
    ],
    confirmLabel: "Mark as Test User"
  },
  disable: {
    title: "Remove Test User Status",
    message: (name) => `Remove Test User status from ${name}?`,
    consequences: [
      "The user counts as a regular user again",
      "Included in reports, usage metrics, and organization cost calculations",
      "Takes effect immediately"
    ],
    confirmLabel: "Remove Test User Status"
  }
}

export default class extends Controller {
  toggle(event) {
    const checkbox = event.target
    const { userId, userName, flag } = checkbox.dataset
    const desired = checkbox.checked

    if (flag === "test_user") {
      // Revert the visual state until the change is confirmed in the modal.
      checkbox.checked = !desired
      this.confirmToggle(checkbox, userId, userName, flag, desired)
      return
    }

    this.submit(checkbox, userId, flag, desired)
  }

  confirmToggle(checkbox, userId, userName, flag, desired) {
    const modalEl = document.querySelector("#testUserToggleModal")

    // Fail safe if the modal isn't on this page for some reason -- apply directly
    // rather than silently dropping the change.
    if (!modalEl || typeof bootstrap === "undefined") {
      checkbox.checked = desired
      this.submit(checkbox, userId, flag, desired)
      return
    }

    const copy = TEST_USER_COPY[desired ? "enable" : "disable"]
    const name = userName || "This user"

    modalEl.querySelector("#testUserToggleTitle").textContent = copy.title
    modalEl.querySelector("#testUserToggleMessage").textContent = copy.message(name)

    const list = modalEl.querySelector("#testUserToggleConsequences")
    list.replaceChildren(...copy.consequences.map((text) => {
      const item = document.createElement("li")
      item.textContent = text
      return item
    }))

    const confirmBtn = modalEl.querySelector("#testUserToggleConfirm")
    confirmBtn.textContent = copy.confirmLabel
    confirmBtn.removeEventListener("click", this.confirmHandler)
    this.confirmHandler = (e) => {
      e.preventDefault()
      checkbox.checked = desired
      this.submit(checkbox, userId, flag, desired)
      bootstrap.Modal.getInstance(modalEl)?.hide()
    }
    confirmBtn.addEventListener("click", this.confirmHandler)

    bootstrap.Modal.getOrCreateInstance(modalEl).show()
  }

  async submit(checkbox, userId, flag, value) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`/admin/users/${userId}/toggle_${flag}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "same-origin",
        body: JSON.stringify({ [flag]: value })
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      if (flag === "test_user") {
        this.refreshSeatStats()
      }
    } catch (error) {
      console.error(`Failed to update ${flag}:`, error)
      checkbox.checked = !value
    }
  }

  // The org Users tab's seat-count cards read test_user straight from the DB,
  // so they go stale the moment this toggle succeeds. Re-fetch and swap them
  // in place rather than reloading the page. No-op on any page that doesn't
  // render #organizationSeatStats (e.g. the global /admin/users index).
  async refreshSeatStats() {
    const statsEl = document.querySelector("#organizationSeatStats")
    if (!statsEl) return

    try {
      const response = await fetch(statsEl.dataset.refreshUrl, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

      statsEl.outerHTML = await response.text()
    } catch (error) {
      console.error("Failed to refresh seat stats:", error)
    }
  }
}
